import { type Viewport } from '@xyflow/react';
import { type Padding } from '@xyflow/system';

import { type AppNode } from '../types';
import { computeFitViewport } from './computeFitViewport';
import { getFitViewPadding } from './getFitViewPadding';
import { getReactFlowPaneScreenSize } from './getVisibleReactFlowPaneScreenSize';
import { toNodeRect } from './layout/nodeLayout';
import { getBoundsFromRects } from './viewportVisibility';

/** Node types that don't represent persisted artwork and shouldn't anchor fit bounds. */
const EXCLUDED_NODE_TYPES = new Set(['canvasMediaUploadPreview', 'expandingBoundingBox', 'imageGeneration']);

export type FitCanvasToNodesParams = {
  nodes: AppNode[];
  setViewport: (viewport: Viewport) => void;
  maxZoom?: number;
  padding?: Padding;
};

/** @returns true if viewport was applied, false if container/bounds not ready (caller should retry) */
export const fitCanvasToNodes = ({ nodes, padding, setViewport, maxZoom }: FitCanvasToNodesParams): boolean => {
  const rects = nodes.filter((node) => !EXCLUDED_NODE_TYPES.has(node.type)).map(toNodeRect);

  if (rects.length === 0) {
    return false;
  }

  const paneSize = getReactFlowPaneScreenSize();

  if (!paneSize) {
    return false;
  }

  const viewport = computeFitViewport({
    bounds: getBoundsFromRects(rects),
    ...paneSize,
    maxZoom,
    padding: padding ?? getFitViewPadding(),
  });

  if (!viewport) {
    return false;
  }

  setViewport(viewport);
  return true;
};
